import { useState } from 'react';
import type { MouseEvent } from 'react';
import { ShoppingCart, Loader2, Plus, Trash2, X, Calculator, Info, Receipt } from 'lucide-react';
import type { EinkaufResponse, EinkaufRow, EinkaufRowPatch, EinkaufLetzteRechnung } from '@/types';
import { einkaufApi } from '@/services/api';
import { formatCurrency, formatDateTime } from '@/utils/currency';
import { parseGermanDecimal } from '@/utils/number';
import { ContactPicker } from '@/components/ContactPicker';

/** axios-Fehlermeldung (deutsch vom API) extrahieren. */
function apiError(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return msg || fallback;
}

interface BauvorhabenEinkaufCardProps {
  bauvorhabenId: number;
  data: EinkaufResponse | null;
  /** Schreiben: nur Finanzen/Admin (Server-Gate bleibt). */
  canWrite: boolean;
  /** Kontakt-Modul aktiv (ContactPicker braucht die Suche). */
  canPickContacts: boolean;
  catalogGewerke: { id: number; name: string }[];
  openContactFromLink: (e: MouseEvent, contactId: number, name?: string) => void;
  onChanged: () => void;
}

/** Zeilen-Dashboard der EK-Ebene (Phase 3): Budget/EK/Skonto/Lieferanten je
 *  Gewerk, verknüpft mit den Eingangsrechnungen. Inline-Editing nur
 *  Finanzen/Admin; Mutationen schreiben direkt + reloaden via onChanged. */
export function BauvorhabenEinkaufCard({
  bauvorhabenId, data, canWrite, canPickContacts, catalogGewerke, openContactFromLink, onChanged,
}: BauvorhabenEinkaufCardProps) {
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Omit<EinkaufRowPatch, 'id'>>({});
  const [rowError, setRowError] = useState('');
  const [rowInfo, setRowInfo] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  // Zeile, für die gerade ein Lieferant gesucht wird (ContactPicker offen)
  const [pickingRowId, setPickingRowId] = useState<number | null>(null);

  // Zeile hinzufügen (nur Finanzen)
  const [showAddRow, setShowAddRow] = useState(false);
  const [addGewerkId, setAddGewerkId] = useState('');
  const [addName, setAddName] = useState('');
  const [addNummer, setAddNummer] = useState('');
  const [isAddingRow, setIsAddingRow] = useState(false);

  if (!data) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-gray-400" />
          Einkauf (EK-Ebene)
        </h2>
        <p className="text-sm text-gray-500">Einkaufsdaten konnten nicht geladen werden.</p>
      </div>
    );
  }

  const { rows, summary, options } = data;
  // Katalog-Gewerke, die noch keine Zeile haben (Add-Dropdown)
  const usedGewerkIds = new Set(rows.map((r) => r.gewerk_id).filter((v): v is number => v !== null));
  const availableGewerke = [...catalogGewerke]
    .filter((g) => !usedGewerkIds.has(g.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'de-DE'));

  const startEdit = (row: EinkaufRow) => {
    setEditingRowId(row.id);
    setRowError('');
    setRowInfo('');
    setDraft({
      budget_netto: row.budget_netto ?? '',
      ek_netto: row.ek_netto ?? '',
      skonto_percent: row.skonto_percent ?? '',
      eigenleistung_netto: row.eigenleistung_netto ?? '',
      ek_note: row.ek_note ?? '',
    });
  };

  const updateRows = async (patches: EinkaufRowPatch[], info: string) => {
    setIsBusy(true);
    setRowError('');
    try {
      await einkaufApi.updateRows(bauvorhabenId, patches);
      setEditingRowId(null);
      setRowInfo(info);
      onChanged();
    } catch (err) {
      setRowError(apiError(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setIsBusy(false);
    }
  };

  const saveEdit = (row: EinkaufRow) => {
    updateRows([{
      id: row.id,
      budget_netto: parseGermanDecimal(String(draft.budget_netto ?? '')),
      ek_netto: parseGermanDecimal(String(draft.ek_netto ?? '')),
      skonto_percent: parseGermanDecimal(String(draft.skonto_percent ?? '')),
      eigenleistung_netto: parseGermanDecimal(String(draft.eigenleistung_netto ?? '')),
      ek_note: String(draft.ek_note ?? '').trim() === '' ? null : String(draft.ek_note).trim(),
    }], 'Zeile gespeichert.');
  };

  /** Σ Eingangsrechnungen (aller Zeilen mit Summe > 0) als EK netto
   *  übernehmen — das Excel-Muster „Einkauf = Schlussrechnung". */
  const adoptInvoiceTotals = async () => {
    const patches: EinkaufRowPatch[] = rows
      .filter((r) => r.eingangsrechnungen_summe_netto > 0)
      .map((r) => ({ id: r.id, ek_netto: r.eingangsrechnungen_summe_netto, ek_source: 'eingangsrechnungen' as const }));
    if (patches.length === 0) {
      setRowInfo('Keine Zeile mit Eingangsrechnungen — nichts zu übernehmen.');
      return;
    }
    if (!window.confirm(`EK netto für ${patches.length} Zeile(n) aus den eingegangenen Rechnungen übernehmen? Bestehende EK-Werte werden überschrieben.`)) return;
    updateRows(patches, 'EK-Werte aus den Rechnungen übernommen.');
  };

  const addRow = async () => {
    setIsAddingRow(true);
    setRowError('');
    try {
      const payload: { gewerk_id?: number; name?: string; quell_nummer?: string } = {};
      if (addGewerkId) payload.gewerk_id = Number(addGewerkId);
      if (addName.trim()) payload.name = addName.trim();
      if (addNummer.trim()) payload.quell_nummer = addNummer.trim();
      await einkaufApi.addRow(bauvorhabenId, payload);
      setAddName('');
      setAddNummer('');
      setAddGewerkId('');
      setShowAddRow(false);
      setRowInfo('Zeile hinzugefügt.');
      onChanged();
    } catch (err) {
      setRowError(apiError(err, 'Hinzufügen fehlgeschlagen.'));
    } finally {
      setIsAddingRow(false);
    }
  };

  const removeRow = async (row: EinkaufRow) => {
    if (!window.confirm(`Freitext-Zeile „${row.gewerk_name ?? row.quell_nummer}" löschen?`)) return;
    setRowError('');
    try {
      await einkaufApi.removeRow(bauvorhabenId, row.id);
      onChanged();
      setRowInfo('Zeile gelöscht.');
    } catch (err) {
      setRowError(apiError(err, 'Löschen fehlgeschlagen.'));
    }
  };

  const addContact = async (row: EinkaufRow, contactId: number) => {
    setRowError('');
    try {
      await einkaufApi.addRowContact(bauvorhabenId, row.id, contactId);
      setPickingRowId(null);
      onChanged();
      setRowInfo('Lieferant zugeordnet.');
    } catch (err) {
      setRowError(apiError(err, 'Zuordnen fehlgeschlagen.'));
    }
  };

  const removeContact = async (row: EinkaufRow, linkId: number) => {
    setRowError('');
    try {
      await einkaufApi.removeRowContact(bauvorhabenId, row.id, linkId);
      onChanged();
      setRowInfo('Lieferant-Zuordnung entfernt.');
    } catch (err) {
      setRowError(apiError(err, 'Entfernen fehlgeschlagen.'));
    }
  };

  const summaryItems: { label: string; value: string; className?: string }[] = [
    { label: 'Σ Budget', value: formatCurrency(summary.summe_budget) },
    { label: 'Σ EK netto', value: formatCurrency(summary.summe_ek) },
    { label: 'Σ Eingangsrechnungen', value: formatCurrency(summary.summe_eingangsrechnungen) },
    { label: 'Σ Skonto', value: formatCurrency(summary.summe_skonto) },
    { label: 'Herstellsumme', value: formatCurrency(summary.herstellsumme) },
    {
      label: 'Σ Gewinn/Verlust',
      value: formatCurrency(summary.summe_gewinn_verlust),
      className: summary.summe_gewinn_verlust < 0 ? 'text-red-600' : 'text-green-700',
    },
  ];

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-gray-400" />
          Einkauf (EK-Ebene)
        </h2>
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowAddRow((v) => !v)}
            className="btn btn-secondary inline-flex items-center gap-1 text-sm"
          >
            {showAddRow ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {showAddRow ? 'Abbrechen' : 'Zeile hinzufügen'}
          </button>
        )}
      </div>

      {!canWrite && (
        <p className="mb-4 text-xs text-gray-500 flex items-center gap-1">
          <Info className="h-3 w-3" />
          Ansicht — Werte pflegt die Rolle Finanzen.
        </p>
      )}

      {rowInfo && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{rowInfo}</div>
      )}
      {rowError && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{rowError}</div>
      )}

      {showAddRow && canWrite && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Katalog-Gewerk</label>
              <select className="input text-sm" value={addGewerkId} onChange={(e) => setAddGewerkId(e.target.value)}>
                <option value="">— ohne Katalog (Freitext) —</option>
                {availableGewerke.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Freitext (ohne Katalog)</label>
              <input
                className="input text-sm"
                placeholder="z. B. Fertigstellungsbürgschaft"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                disabled={!!addGewerkId}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Nummer aus PDF (optional)</label>
              <input
                className="input text-sm"
                placeholder="z. B. 05"
                value={addNummer}
                onChange={(e) => setAddNummer(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-primary inline-flex items-center gap-1 text-sm"
              disabled={isAddingRow || (!addGewerkId && !addName.trim())}
              onClick={addRow}
            >
              {isAddingRow && <Loader2 className="h-4 w-4 animate-spin" />}
              Hinzufügen
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          Noch keine EK-Zeilen. Netto-EK-PDF hochladen (KI-Extraktion, siehe Extraktion-Karte) oder Zeile manuell hinzufügen.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2 pr-3 font-medium">Gewerk</th>
                <th className="py-2 pr-3 font-medium">Nr.</th>
                <th className="py-2 pr-3 font-medium text-right">Budget netto</th>
                <th className="py-2 pr-3 font-medium text-right">EK netto</th>
                <th className="py-2 pr-3 font-medium text-right">Σ Rechnungen</th>
                <th className="py-2 pr-3 font-medium text-right">Skonto</th>
                <th className="py-2 pr-3 font-medium text-right">G/V</th>
                <th className="py-2 pr-3 font-medium">Letzte Rechnung</th>
                <th className="py-2 pr-3 font-medium">Lieferanten</th>
                <th className="py-2 font-medium">Bemerkung</th>
                {canWrite && <th className="py-2" aria-label="Aktionen" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const edited = editingRowId === row.id;
                const gv = row.gewinn_verlust === null ? null : parseFloat(row.gewinn_verlust);
                return (
                  <tr key={row.id} className="border-b border-gray-100 align-top">
                    <td className="py-2 pr-3">
                      <p className="font-medium text-gray-900">{row.gewerk_name}</p>
                      {row.phase_name && <p className="text-xs text-gray-500">{row.phase_name}</p>}
                      {row.is_free_text && (
                        <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 text-xs text-amber-800">Freitext</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-600">{row.quell_nummer ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">
                      {edited ? (
                        <input className="input w-28 text-right text-sm" value={String(draft.budget_netto ?? '')} onChange={(e) => setDraft((d) => ({ ...d, budget_netto: e.target.value }))} />
                      ) : (
                        <span>
                          {formatCurrency(row.budget_netto)}
                          {row.budget_source === 'ki_netto_ek_pdf' && (
                            <span className="ml-1 text-xs text-gray-400" title="Übernommen aus dem Netto-EK-PDF (KI)">KI</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {edited ? (
                        <input className="input w-28 text-right text-sm" value={String(draft.ek_netto ?? '')} onChange={(e) => setDraft((d) => ({ ...d, ek_netto: e.target.value }))} />
                      ) : (
                        <span>
                          {formatCurrency(row.ek_netto)}
                          {row.ek_source === 'eingangsrechnungen' && (
                            <span className="ml-1 text-xs text-gray-400" title="Σ Eingangsrechnungen">RE</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right text-gray-700">{formatCurrency(row.eingangsrechnungen_summe_netto)}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {edited ? (
                        <input className="input w-20 text-right text-sm" value={String(draft.skonto_percent ?? '')} onChange={(e) => setDraft((d) => ({ ...d, skonto_percent: e.target.value }))} />
                      ) : row.skonto_percent !== null ? (
                        <span>
                          {formatCurrency(row.skonto_betrag)}
                          <span className="ml-1 text-xs text-gray-400">
                            {parseFloat(row.skonto_percent).toLocaleString('de-DE')} %
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className={`py-2 pr-3 text-right font-medium ${gv === null ? '' : gv < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {formatCurrency(gv)}
                    </td>
                    <td className="py-2 pr-3">
                      {row.letzte_rechnung ? (
                        <LastRechnungLink letzte={row.letzte_rechnung} />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-col gap-1">
                        {row.lieferanten.map((l) => (
                          <span key={l.link_id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs">
                            <a
                              href={`#kontakt-${l.contact_id}`}
                              onClick={(e) => openContactFromLink(e, l.contact_id, l.company_name || l.display_name || undefined)}
                              className="hover:text-primary-700"
                            >
                              {l.company_name || l.display_name || `Kontakt #${l.contact_id}`}
                            </a>
                            {canWrite && (
                              <button type="button" onClick={() => removeContact(row, l.link_id)} className="text-gray-400 hover:text-red-600" title="Zuordnung entfernen">
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        ))}
                        {canWrite && canPickContacts && (
                          pickingRowId === row.id ? (
                            <div className="w-56">
                              <ContactPicker onSelect={(contact) => addContact(row, contact.id)} placeholder="Lieferant suchen…" />
                              <button type="button" onClick={() => setPickingRowId(null)} className="mt-1 text-xs text-gray-500 hover:text-gray-700">Abbrechen</button>
                            </div>
                          ) : (
                            <button type="button" onClick={() => setPickingRowId(row.id)} className="text-xs text-primary-600 hover:text-primary-700">
                              + Lieferant
                            </button>
                          )
                        )}
                      </div>
                    </td>
                    <td className="py-2">
                      {edited ? (
                        <input className="input w-40 text-sm" value={String(draft.ek_note ?? '')} onChange={(e) => setDraft((d) => ({ ...d, ek_note: e.target.value }))} />
                      ) : (
                        <span className="text-xs text-gray-600">{row.ek_note ?? '—'}</span>
                      )}
                    </td>
                    {canWrite && (
                      <td className="py-2">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          {edited ? (
                            <>
                              <button type="button" className="btn btn-primary text-xs" disabled={isBusy} onClick={() => saveEdit(row)}>
                                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Speichern'}
                              </button>
                              <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setEditingRowId(null)}>Abbrechen</button>
                            </>
                          ) : (
                            <>
                              <button type="button" className="text-xs text-primary-600 hover:text-primary-700" onClick={() => startEdit(row)}>Bearbeiten</button>
                              {row.is_free_text && row.lieferanten.length === 0 && (
                                <button type="button" onClick={() => removeRow(row)} className="text-gray-400 hover:text-red-600" title="Freitext-Zeile löschen">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && rows.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={adoptInvoiceTotals}
            className="btn btn-secondary inline-flex items-center gap-2 text-sm"
            disabled={isBusy}
          >
            <Calculator className="h-4 w-4" />
            Σ Rechnungen als EK übernehmen
          </button>
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-6">
        {summaryItems.map((item) => (
          <div key={item.label} className="rounded-lg bg-gray-50 px-3 py-2">
            <p className="text-xs text-gray-500">{item.label}</p>
            <p className={`mt-0.5 text-sm font-semibold ${item.className ?? 'text-gray-900'}`}>{item.value}</p>
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-gray-400">
        G/V = Budget − EK netto − Eigenleistung. Skonto-Betrag = Prozentsatz auf EK netto (Standard {options.default_skonto_percent.toLocaleString('de-DE')} %).
      </p>
    </div>
  );
}

/** „Schlussrechnung"-Spalte: neueste Eingangsrechnung der Zeile (nur Link in
 *  die Rechnungen-Spalte — kein Inline-Detail). */
function LastRechnungLink({ letzte }: { letzte: EinkaufLetzteRechnung }) {
  return (
    <a href="/rechnungen" className="inline-flex flex-col gap-0.5 text-xs leading-tight text-primary-600 hover:text-primary-700">
      <span className="inline-flex items-center gap-1">
        <Receipt className="h-3 w-3" />
        {letzte.invoice_number ?? '—'}
      </span>
      <span className="text-gray-500">
        {formatDateTime(letzte.invoice_date ?? undefined)} · {formatCurrency(letzte.amount_gross)}
      </span>
    </a>
  );
}